import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { SessionActions } from '@next/core-lib/session';
import { UserActions } from '@next/core-lib/user';
import { ofType, Actions, createEffect } from '@ngrx/effects';

import { select, Store } from '@ngrx/store';
import { EMPTY, of, forkJoin } from 'rxjs';
import { switchMap, map, withLatestFrom, take } from 'rxjs/operators';

import * as AppActions from './app.actions';

import * as AppSelectors from './app.selectors';
import * as ProprietaryFertilizerActions from '../proprietary-fertilizer/store/proprietary-fertilizer.actions';
import { SiteState } from '../shared/models/app.model';

import { PeriodService } from '../shared/services/arable-period.service';

@Injectable()
export class AppEffects {
  protected readonly store = inject<Store<SiteState>>(Store);
  private readonly actions$ = inject(Actions);
  protected readonly router = inject(Router);
  protected readonly periodService = inject(PeriodService);

  initCropYear$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.userInitialized),
      map(() => AppActions.getDefaultCropYear()),
    ),
  );

  getDefaultCropYear$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.getDefaultCropYear),
      switchMap((_) =>
        this.periodService
          .getDefaultYear()
          .pipe(
            map((defaultYear: number) => AppActions.setDefaultCropYear({ cropYear: defaultYear })),
          ),
      ),
    ),
  );

  setDefaultCropYear$ = createEffect(() => {
    const currentYear = new Date().getFullYear();
    return this.actions$.pipe(
      ofType(AppActions.setDefaultCropYear),
      map(() =>
        AppActions.setAvailableCropYears({
          availableCropYears: [currentYear - 1, currentYear, currentYear + 1],
        }),
      ),
    );
  });

  userInitialized$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.userInitialized),
      map(() => ProprietaryFertilizerActions.fetchOriginalCropAids()),
    ),
  );

  sessionAndCustomerInitialized$ = createEffect(() =>
    forkJoin([
      this.actions$.pipe(ofType(SessionActions.sessionInitialized), take(1)),
      this.actions$.pipe(ofType(UserActions.customerInitialized), take(1)),
      this.actions$.pipe(ofType(AppActions.setDefaultCropYear), take(1)),
    ]).pipe(map((_) => ProprietaryFertilizerActions.fetchProprietaryFertilizers())),
  );

  customerChanged$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.customerChanged),
      map(() => ProprietaryFertilizerActions.fetchProprietaryFertilizers()),
    ),
  );

  setCropYear$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.setCropYear),
      withLatestFrom(
        this.store.pipe(select(AppSelectors.getCropYear)),
        this.store.pipe(select(AppSelectors.getAvailableCropYears)),
        this.store.pipe(select(AppSelectors.getDefaultCropYear)),
      ),
      switchMap(([{ cropYear }, currentCropYear, availableCropYears, defaultCropYear]) => {
        if (
          isNaN(cropYear) ||
          availableCropYears.indexOf(cropYear) === -1 ||
          cropYear === currentCropYear
        ) {
          return EMPTY;
        }

        return of(
          SessionActions.setSession({
            data: {
              arable:
                cropYear === defaultCropYear
                  ? {}
                  : {
                      cropYear,
                    },
            },
          }),
          ProprietaryFertilizerActions.fetchProprietaryFertilizers(),
        );
      }),
    ),
  );
}
